import React from 'react';

type ButtonAriaProps = React.AriaAttributes &
    Pick<React.HTMLAttributes<HTMLElement>, 'role' | 'id' | 'tabIndex' | 'onKeyDown'>;

interface BtnProps {
    buttonClasses?: string;
    buttonText?: React.ReactNode;
    buttonType?: 'button' | 'submit' | 'reset';
    buttonAs?: 'button' | 'a' | 'div' | 'span';
    href?: string;
    buttonOnClick?: (event: React.MouseEvent<HTMLElement>) => void;
    buttonIcon?: React.ReactNode;
    buttonIconPosition?: 'left' | 'right';
    buttonDisabled?: boolean;
    buttonTitle?: string;
    /** Native browser tooltip on hover. For icon-only buttons, defaults to `ariaLabel`. */
    buttonTooltip?: string;
    /** Convenience shorthand for `buttonAriaProps['aria-label']` */
    ariaLabel?: string;
    buttonAriaProps?: ButtonAriaProps;
    variant?:
        | 'primary'
        | 'secondary'
        | 'secondary-outlined'
        | 'secondary-soft'
        | 'secondary-soft-outlined'
        | 'tertiary'
        | 'tertiary-outlined'
        | 'outlined-faint'
        | 'dashed'
        | 'dotted'
        | 'danger'
        | 'danger-outline'
        | 'danger-outlined'
        | 'success'
        | 'success-outlined'
        | 'warning'
        | 'warning-outlined'
        | 'info'
        | 'ghost'
        | 'icon'
        | 'link';
    buttonSize?: 'sm' | 'md' | 'lg';
    /** Corner radius shape; default uses `--button-radius` (12px). */
    buttonShape?: 'default' | 'pill' | 'semi-square';
}

const Btn = (props: BtnProps) => {
    const {
        buttonClasses,
        buttonText,
        buttonType = 'button',
        buttonAs = 'button',
        href,
        buttonOnClick,
        buttonIcon,
        buttonIconPosition = 'left',
        buttonDisabled = false,
        buttonTitle,
        buttonTooltip,
        ariaLabel,
        buttonAriaProps,
        variant,
        buttonSize,
        buttonShape,
    } = props;

    const isIconOnly = !buttonText && Boolean(buttonIcon);
    const resolvedAriaLabel =
        ariaLabel ?? (typeof buttonAriaProps?.['aria-label'] === 'string' ? buttonAriaProps['aria-label'] : undefined);
    const hoverTitle =
        buttonTooltip ??
        buttonTitle ??
        (isIconOnly && typeof resolvedAriaLabel === 'string' ? resolvedAriaLabel : undefined);

    const shapeClass =
        buttonShape === 'pill'
            ? 'btn-pill'
            : buttonShape === 'semi-square'
              ? 'btn-semi-square'
              : undefined;

    const className = [
        'btn',
        variant ? `btn-${variant}` : undefined,
        buttonSize ? `btn-${buttonSize}` : undefined,
        shapeClass,
        isIconOnly ? 'btn-icon-only' : undefined,
        buttonClasses,
    ]
        .filter(Boolean)
        .join(' ');
    const leftIcon = Boolean(buttonIcon) && buttonIconPosition === 'left';
    const rightIcon = Boolean(buttonIcon) && buttonIconPosition === 'right';
    const iconWrap = buttonIcon ? (
        <span className="btn-icon-wrap" aria-hidden="true">
            {buttonIcon}
        </span>
    ) : null;

    return React.createElement(
        buttonAs,
        {
            className,
            onClick: buttonDisabled ? undefined : buttonOnClick,
            ...(buttonAs === 'button' ? { type: buttonType, disabled: buttonDisabled } : {}),
            ...(buttonAs === 'a' && href ? { href } : {}),
            ...(resolvedAriaLabel ? { 'aria-label': resolvedAriaLabel } : {}),
            ...buttonAriaProps,
            ...(hoverTitle ? { title: hoverTitle } : {}),
        },
        <>
            {leftIcon && iconWrap}
            {buttonText && <span className="btn-text">{buttonText}</span>}
            {rightIcon && iconWrap}
        </>,
    );
};

export default Btn;
export type { BtnProps, ButtonAriaProps };
